<template>
  <BasicTemplate>
    <h3>
      <template v-if="safeReturnTo">
        <router-link :to="safeReturnTo">Back to game</router-link>
        /
      </template>
      <router-link to="/analytics-configs">Analytics configs</router-link>
      /
      Analytics config {{ isNew ? 'new' : '#' + $route.params.id }}
    </h3>

    <div class="analytics-config-form__tabs-shell">
      <v-tabs v-model="activeTab" background-color="transparent" class="analytics-config-form__tabs">
        <v-tab>Config</v-tab>
        <v-tab :disabled="isNew">Games</v-tab>
      </v-tabs>

      <transition name="analytics-config-tab-slide" mode="out-in">
        <div v-if="activeTab === 0" key="tab-config" class="analytics-config-form__tab-panel">
          <div class="analytics-config-form__tab-panel-body analytics-config-form__tab-panel-body--padded">
            <div v-if="loading" class="d-flex justify-center pa-8">
              <v-progress-circular indeterminate color="primary" />
            </div>
            <v-form v-else ref="form" v-model="valid">
              <v-card class="mb-4 analytics-config-form__section" elevation="1">
                <v-card-title class="d-flex align-center analytics-config-form__section-title">
                  <v-icon class="mr-2">mdi-chart-timeline-variant</v-icon>
                  <span class="font-weight-bold">Analytics config</span>
                </v-card-title>

                <v-card-text>
                  <v-row dense>
                    <v-col cols="12" sm="6" md="3">
                      <v-select
                        v-model="item.type"
                        :items="typeItems"
                        label="Type *"
                        :rules="[rules.required]"
                        :disabled="!isNew"
                        dense
                        hide-details="auto"
                        data-testid="analytics-config-type"
                      />
                    </v-col>
                    <v-col cols="12" sm="6" md="7">
                      <v-text-field
                        v-model="item.key"
                        label="Key *"
                        :rules="[rules.required]"
                        dense
                        hide-details="auto"
                        hint="Instrumentation key / Measurement ID / DSN"
                        persistent-hint
                        data-testid="analytics-config-key"
                      />
                    </v-col>
                    <v-col cols="12" sm="6" md="2">
                      <v-text-field
                        v-model.number="item.sampling"
                        type="number"
                        min="0"
                        max="100"
                        label="Sampling % *"
                        :rules="[rules.required, rules.sampling]"
                        dense
                        hide-details="auto"
                        data-testid="analytics-config-sampling"
                      />
                    </v-col>
                    <v-col cols="12">
                      <v-text-field
                        v-model="item.description"
                        label="Description"
                        dense
                        hide-details="auto"
                        clearable
                        data-testid="analytics-config-description"
                      />
                    </v-col>
                  </v-row>

                  <div class="d-flex align-center mt-6 mb-2">
                    <h4 class="mb-0">Domain samplings</h4>
                    <v-btn
                      class="ml-4"
                      small
                      color="primary"
                      outlined
                      data-testid="analytics-config-add-domain"
                      @click="addDomainRow"
                    >
                      <v-icon left small>mdi-plus</v-icon>
                      Add domain
                    </v-btn>
                  </div>
                  <p class="text--secondary mb-3" style="font-size: 0.875rem">
                    Optional overrides. If the current domain is not listed, the default sampling above is used.
                  </p>

                  <v-simple-table dense v-if="item.domainSamplings.length">
                    <thead>
                      <tr>
                        <th scope="col" style="width: 55%">Domain</th>
                        <th scope="col" style="width: 20%">Sampling %</th>
                        <th scope="col" style="width: 25%" />
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="(row, index) in item.domainSamplings" :key="row._uid">
                        <td>
                          <v-text-field
                            v-model="row.domain"
                            dense
                            hide-details="auto"
                            :rules="[rules.required]"
                            placeholder="e.g. www.arkadium.com"
                          />
                        </td>
                        <td>
                          <v-text-field
                            v-model.number="row.sampling"
                            type="number"
                            min="0"
                            max="100"
                            dense
                            hide-details="auto"
                            :rules="[rules.required, rules.sampling]"
                          />
                        </td>
                        <td class="text-right text-no-wrap">
                          <v-tooltip v-if="row.id" bottom>
                            <template v-slot:activator="{ on, attrs }">
                              <v-btn
                                icon
                                small
                                color="secondary"
                                tag="a"
                                :href="domainSamplingChangeLogHref(row)"
                                target="_blank"
                                rel="noopener noreferrer"
                                :data-testid="`analytics-config-domain-history-${row.id}`"
                                v-bind="attrs"
                                v-on="on"
                              >
                                <v-icon small>mdi-history</v-icon>
                              </v-btn>
                            </template>
                            <span>View change history</span>
                          </v-tooltip>
                          <v-tooltip bottom>
                            <template v-slot:activator="{ on, attrs }">
                              <v-btn
                                icon
                                small
                                color="warning"
                                v-bind="attrs"
                                v-on="on"
                                @click="removeDomainRow(index)"
                              >
                                <v-icon small>mdi-delete</v-icon>
                              </v-btn>
                            </template>
                            <span>Remove domain</span>
                          </v-tooltip>
                        </td>
                      </tr>
                    </tbody>
                  </v-simple-table>
                  <v-alert v-else type="info" dense outlined text class="mb-0">
                    No domain overrides yet.
                  </v-alert>
                </v-card-text>
              </v-card>

              <div class="analytics-config-form__save mb-2">
                <v-btn
                  color="primary"
                  large
                  class="mr-2"
                  :loading="saving"
                  data-testid="analytics-config-save"
                  @click="save"
                >
                  Save
                </v-btn>
                <v-btn
                  outlined
                  large
                  class="mr-2"
                  :to="cancelTarget"
                  data-testid="analytics-config-cancel"
                >
                  Cancel
                </v-btn>
                <v-btn
                  v-if="!isNew && configId"
                  outlined
                  large
                  color="secondary"
                  :to="changeLogLink"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="analytics-config-change-history"
                >
                  <v-icon left>mdi-history</v-icon>
                  History
                </v-btn>
              </div>
            </v-form>
          </div>
        </div>

        <div v-else-if="activeTab === 1" key="tab-games" class="analytics-config-form__tab-panel">
          <div v-if="loading" class="d-flex justify-center pa-8">
            <v-progress-circular indeterminate color="primary" />
          </div>
          <div v-else class="analytics-config-form__tab-panel-body">
            <v-card class="mb-0 analytics-config-form__games" elevation="1">
              <v-card-title class="d-flex align-center">
                <v-icon class="mr-2">mdi-gamepad-variant</v-icon>
                <span class="font-weight-bold">Games</span>
                <v-spacer />
                <v-dialog v-model="addGamesDialog" width="800" persistent>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      color="success"
                      data-testid="analytics-config-attach-game"
                      v-bind="attrs"
                      v-on="on"
                    >
                      Assign
                    </v-btn>
                  </template>
                  <v-card class="admin-dialog">
                    <v-card-title class="d-flex align-center admin-dialog__title">
                      <span class="font-weight-bold">
                        Assign games to analytics config #{{ configId }}
                      </span>
                      <v-spacer />
                      <v-btn icon small @click="closeAddGamesDialog">
                        <v-icon>mdi-close</v-icon>
                      </v-btn>
                    </v-card-title>
                    <v-card-text class="admin-dialog__body">
                      <v-text-field
                        v-model="addGamesSearch"
                        label="Search"
                        dense
                        outlined
                        hide-details
                        class="mb-3"
                        prepend-inner-icon="mdi-magnify"
                      />
                      <v-data-table
                        class="admin-dialog__table"
                        dense
                        sort-by="id"
                        show-select
                        :search="addGamesSearch"
                        :headers="addGamesHeaders"
                        :items="availableGames"
                        :loading="availableGamesLoading"
                        v-model="selectedGames"
                        :items-per-page="10"
                        :footer-props="{ itemsPerPageOptions: [10, 25, 50, -1] }"
                      />
                    </v-card-text>
                    <v-card-actions class="admin-dialog__actions">
                      <v-spacer />
                      <v-btn
                        color="primary"
                        class="mr-2"
                        data-testid="analytics-config-add-games-save"
                        :disabled="!selectedGames.length"
                        :loading="linkBusy"
                        @click="addGames"
                      >
                        Save
                      </v-btn>
                      <v-btn outlined :disabled="linkBusy" @click="closeAddGamesDialog">Cancel</v-btn>
                    </v-card-actions>
                  </v-card>
                </v-dialog>
              </v-card-title>

              <v-card-text class="analytics-config-form__games-search py-1">
                <v-text-field
                  v-model="gameLinkSearch"
                  label="Search"
                  dense
                  hide-details
                  class="mt-0"
                  prepend-icon="mdi-magnify"
                />
              </v-card-text>

              <v-data-table
                class="analytics-config-form__games-table"
                :headers="gameLinkHeaders"
                :items="gameLinks"
                :search="gameLinkSearch"
                item-key="gameId"
                sort-by="gameId"
                :items-per-page="10"
                :footer-props="{ itemsPerPageOptions: [10, 25, 50, -1] }"
              >
                <template v-slot:item.name="{ item: link }">
                  {{ link.game?.name || '—' }}
                </template>
                <template v-slot:item.slug="{ item: link }">
                  {{ link.game?.slug || '—' }}
                </template>
                <template v-slot:item.actions="{ item: link }">
                  <div class="analytics-config-form__game-actions">
                    <v-tooltip bottom>
                      <template v-slot:activator="{ on, attrs }">
                        <v-icon
                          color="primary"
                          v-bind="attrs"
                          v-on="on"
                          :data-testid="`analytics-config-open-game-${link.gameId}`"
                          @click="$router.push(`/games/${link.gameId}?tab=analytics`)"
                        >
                          mdi-open-in-new
                        </v-icon>
                      </template>
                      <span>Open game Analytics</span>
                    </v-tooltip>
                    <v-tooltip bottom>
                      <template v-slot:activator="{ on, attrs }">
                        <v-icon
                          color="secondary"
                          tag="a"
                          :href="gameLinkChangeLogHref(link)"
                          target="_blank"
                          rel="noopener noreferrer"
                          :data-testid="`analytics-config-game-link-history-${link.gameId}`"
                          v-bind="attrs"
                          v-on="on"
                        >
                          mdi-history
                        </v-icon>
                      </template>
                      <span>View change history</span>
                    </v-tooltip>
                    <v-tooltip bottom>
                      <template v-slot:activator="{ on, attrs }">
                        <v-icon
                          color="warning"
                          v-bind="attrs"
                          v-on="on"
                          :disabled="unlinkBusyId === link.gameId"
                          :data-testid="`analytics-config-unlink-game-${link.gameId}`"
                          @click="unlinkGame(link)"
                        >
                          mdi-delete
                        </v-icon>
                      </template>
                      <span>Delete</span>
                    </v-tooltip>
                  </div>
                </template>
                <template v-slot:no-data>
                  <div class="text-center pa-6">
                    <v-icon size="64" color="grey lighten-1">mdi-gamepad-variant-outline</v-icon>
                    <p class="mt-2 mb-0 grey--text">No games assigned yet. Assign a game to get started.</p>
                  </div>
                </template>
              </v-data-table>
            </v-card>
          </div>
        </div>
      </transition>
    </div>
  </BasicTemplate>
</template>

<script>
import BasicTemplate from '../../templates/basic.vue';
import persistentDialogEsc from '@/mixins/persistentDialogEsc';
import {
  ANALYTICS_CONFIG_TYPES,
  apiErrorMessage,
} from '../../constants/analyticsConfigTypes';

let domainUid = 1;

const CONFIG_TABS = ['config', 'games'];

function tabIndexFromQuery(tab, allowSecondaryTabs) {
  const key = String(tab || 'config').toLowerCase();
  const index = CONFIG_TABS.indexOf(key);
  if (index <= 0) return 0;
  return allowSecondaryTabs ? index : 0;
}

function tabNameFromIndex(index) {
  return CONFIG_TABS[index] || 'config';
}

function emptyItem(type = 'AppInsights') {
  return {
    type,
    key: '',
    description: '',
    sampling: 2,
    domainSamplings: [],
  };
}

function normalizeDomainRows(rows) {
  return (Array.isArray(rows) ? rows : []).map((row) => ({
    id: row.id,
    domain: row.domain || '',
    sampling: row.sampling == null ? 0 : Number(row.sampling),
    _uid: domainUid++,
  }));
}

function normalizeGameLinks(rows) {
  return (Array.isArray(rows) ? rows : [])
    .map((row) => ({
      gameId: row.gameId,
      analyticsConfigId: row.analyticsConfigId,
      type: row.type,
      game: row.game || null,
    }))
    .sort((a, b) => a.gameId - b.gameId);
}

export default {
  name: 'AnalyticsConfigEdit',
  components: { BasicTemplate },
  mixins: [
    persistentDialogEsc({
      addGamesDialog: { close: 'closeAddGamesDialog', busy: 'linkBusy' },
    }),
  ],
  data() {
    return {
      activeTab: 0,
      loading: false,
      saving: false,
      valid: true,
      item: emptyItem(),
      gameLinks: [],
      availableGames: [],
      availableGamesLoading: false,
      addGamesDialog: false,
      selectedGames: [],
      addGamesSearch: '',
      linkBusy: false,
      unlinkBusyId: null,
      gameLinkSearch: '',
      typeItems: ANALYTICS_CONFIG_TYPES,
      addGamesHeaders: [
        { text: 'Id', value: 'id', width: '72px' },
        { text: 'Slug', value: 'slug' },
        { text: 'Name', value: 'name' },
      ],
      gameLinkHeaders: [
        { text: 'Id', value: 'gameId' },
        { text: 'Name', value: 'name' },
        { text: 'Slug', value: 'slug' },
        {
          text: 'Actions',
          value: 'actions',
          sortable: false,
          align: 'center',
          width: '1%',
          class: 'analytics-config-form__games-col--compact',
          cellClass: 'analytics-config-form__games-col--compact',
        },
      ],
      rules: {
        required: (v) => (v !== null && v !== undefined && String(v).trim() !== '') || 'Required',
        sampling: (v) => {
          const n = Number(v);
          if (Number.isNaN(n)) return 'Must be a number';
          if (n < 0 || n > 100) return 'Must be 0–100';
          return true;
        },
      },
    };
  },
  computed: {
    isNew() {
      // Dedicated /new route has no :id param; /:id also accepts literal "new".
      return !this.$route.params.id || this.$route.params.id === 'new';
    },
    safeReturnTo() {
      const raw = this.$route.query.returnTo;
      if (typeof raw !== 'string') return null;
      // Only allow in-app game paths (avoid open redirects).
      if (!/^\/games\/\d+(\?tab=analytics)?$/.test(raw)) return null;
      return raw;
    },
    cancelTarget() {
      return this.safeReturnTo || '/analytics-configs';
    },
    configId() {
      return this.isNew ? null : Number(this.$route.params.id);
    },
    changeLogLink() {
      return {
        path: '/game-data-change-log',
        query: {
          table: 'analytics_config',
          entityId: String(this.configId),
        },
      };
    },
  },
  watch: {
    '$route.params.id': {
      immediate: true,
      handler() {
        this.load();
        this.syncTabFromRoute();
      },
    },
    '$route.query.tab'() {
      this.syncTabFromRoute();
    },
    activeTab(index) {
      this.syncTabToRoute(index);
    },
    addGamesDialog(open) {
      if (open) {
        this.loadAvailableGames();
      }
    },
  },
  created() {
    this.syncTabFromRoute();
  },
  methods: {
    syncTabFromRoute() {
      const allowSecondaryTabs = !this.isNew;
      const next = tabIndexFromQuery(this.$route.query.tab, allowSecondaryTabs);
      if (this.activeTab !== next) {
        this.activeTab = next;
      }
    },
    syncTabToRoute(index) {
      if (this.isNew && index !== 0) {
        this.activeTab = 0;
        return;
      }
      const tab = tabNameFromIndex(index);
      const current = String(this.$route.query.tab || 'config').toLowerCase();
      if (current === tab) return;
      this.$router.replace({
        query: {
          ...this.$route.query,
          tab,
        },
      }).catch(() => {});
    },
    async load() {
      if (this.isNew) {
        const typeFromQuery = this.$route.query.type;
        const allowed = ANALYTICS_CONFIG_TYPES.some((t) => t.value === typeFromQuery);
        this.item = emptyItem(allowed ? typeFromQuery : 'AppInsights');
        this.gameLinks = [];
        this.closeAddGamesDialog();
        this.loading = false;
        this.activeTab = 0;
        return;
      }
      this.loading = true;
      try {
        const data = await this.$gameApi.get(`/analytics-configs/${this.$route.params.id}`);
        this.item = {
          type: data.type,
          key: data.key || '',
          description: data.description || '',
          sampling: data.sampling == null ? 0 : Number(data.sampling),
          domainSamplings: normalizeDomainRows(data.domainSamplings),
        };
        this.gameLinks = normalizeGameLinks(data.gameLinks);
        this.selectedGames = [];
        this.availableGames = [];
      } catch (e) {
        this.$swal.fire({
          icon: 'error',
          title: apiErrorMessage(e, 'Failed to load config'),
          timer: 2500,
          showConfirmButton: false,
        });
        this.$router.push('/analytics-configs');
      } finally {
        this.loading = false;
      }
    },
    async refreshLinks() {
      const data = await this.$gameApi.get(`/analytics-configs/${this.$route.params.id}`);
      this.gameLinks = normalizeGameLinks(data.gameLinks);
    },
    async loadAvailableGames() {
      if (!this.item?.type) {
        this.availableGames = [];
        return;
      }
      this.availableGamesLoading = true;
      try {
        const type = encodeURIComponent(this.item.type);
        const list = await this.$gameApi.get(`/analytics-configs/available-games?type=${type}`);
        this.availableGames = Array.isArray(list) ? list : [];
      } catch (e) {
        this.availableGames = [];
        this.$swal.fire({
          icon: 'error',
          title: apiErrorMessage(e, 'Failed to load available games'),
          timer: 2500,
          showConfirmButton: false,
        });
      } finally {
        this.availableGamesLoading = false;
      }
    },
    closeAddGamesDialog() {
      if (this.linkBusy) return;
      this.addGamesDialog = false;
      this.selectedGames = [];
      this.addGamesSearch = '';
    },
    async addGames() {
      if (!this.selectedGames.length || !this.configId) return;
      this.linkBusy = true;
      try {
        const results = await Promise.allSettled(
          this.selectedGames.map((g) =>
            this.$gameApi.post(`/games/${g.id}/analytics-configs/${this.configId}`, {}),
          ),
        );
        const failed = results
          .map((result, index) => ({ result, game: this.selectedGames[index] }))
          .filter(({ result }) => result.status === 'rejected');
        const added = results.length - failed.length;

        if (added) {
          await this.refreshLinks();
          await this.loadAvailableGames();
        }

        if (!failed.length) {
          this.$swal.fire({
            icon: 'success',
            title: 'Games assigned',
            timer: 1200,
            showConfirmButton: false,
          });
          this.linkBusy = false;
          this.closeAddGamesDialog();
          return;
        }

        this.selectedGames = failed.map(({ game }) => game);
        const firstError = failed[0].result.reason;
        this.$swal.fire({
          icon: added ? 'warning' : 'error',
          title: added
            ? `Assigned ${added}, failed ${failed.length}`
            : apiErrorMessage(firstError, 'Assign games failed'),
          text: added
            ? apiErrorMessage(firstError, 'Some games already have this config type')
            : undefined,
          timer: 4000,
          showConfirmButton: false,
        });
      } finally {
        this.linkBusy = false;
      }
    },
    async unlinkGame(link) {
      if (!link?.gameId || !this.configId) return;
      const name = link.game?.name || `#${link.gameId}`;
      const { isConfirmed } = await this.$swal.fire({
        icon: 'warning',
        title: `Delete ${name}?`,
        text: 'The catalog config itself will not be deleted.',
        showCancelButton: true,
        confirmButtonText: 'Delete',
      });
      if (!isConfirmed) return;
      this.unlinkBusyId = link.gameId;
      try {
        await this.$gameApi.delete(
          `/games/${link.gameId}/analytics-configs/${this.configId}`,
        );
        this.$swal.fire({
          icon: 'success',
          title: 'Deleted',
          timer: 1200,
          showConfirmButton: false,
        });
        await this.refreshLinks();
      } catch (e) {
        this.$swal.fire({
          icon: 'error',
          title: apiErrorMessage(e, 'Delete failed'),
          timer: 3500,
          showConfirmButton: false,
        });
      } finally {
        this.unlinkBusyId = null;
      }
    },
    domainSamplingChangeLogLink(row) {
      return {
        path: '/game-data-change-log',
        query: {
          table: 'analytics_config_domain_sampling',
          entityId: String(row.id),
        },
      };
    },
    domainSamplingChangeLogHref(row) {
      return this.$router.resolve(this.domainSamplingChangeLogLink(row)).href;
    },
    gameLinkChangeLogLink(link) {
      return {
        path: '/game-data-change-log',
        query: {
          table: 'game_analytics_config',
          entityId: JSON.stringify({
            gameId: Number(link.gameId),
            analyticsConfigId: Number(this.configId),
          }),
        },
      };
    },
    gameLinkChangeLogHref(link) {
      return this.$router.resolve(this.gameLinkChangeLogLink(link)).href;
    },
    addDomainRow() {
      this.item.domainSamplings.push({
        domain: '',
        sampling: 100,
        _uid: domainUid++,
      });
    },
    removeDomainRow(index) {
      this.item.domainSamplings.splice(index, 1);
    },
    buildPayload() {
      const domains = this.item.domainSamplings.map((row) => {
        const payload = {
          domain: String(row.domain || '').trim(),
          sampling: Math.max(0, Math.min(100, Math.floor(Number(row.sampling) || 0))),
        };
        if (row.id != null) payload.id = row.id;
        return payload;
      });

      const seen = new Set();
      for (const row of domains) {
        const key = row.domain.toLowerCase();
        if (seen.has(key)) {
          throw new Error(`Duplicate domain: ${row.domain}`);
        }
        seen.add(key);
      }

      return {
        type: this.item.type,
        key: String(this.item.key || '').trim(),
        description: this.item.description ? String(this.item.description).trim() : null,
        sampling: Math.max(0, Math.min(100, Math.floor(Number(this.item.sampling) || 0))),
        domainSamplings: domains,
      };
    },
    async save() {
      if (this.$refs.form && !this.$refs.form.validate()) return;
      this.saving = true;
      try {
        const payload = this.buildPayload();
        if (this.isNew) {
          const created = await this.$gameApi.post('/analytics-configs', payload);
          this.$swal.fire({
            icon: 'success',
            title: 'Created',
            timer: 1200,
            showConfirmButton: false,
          });
          if (this.safeReturnTo) {
            this.$router.replace(this.safeReturnTo);
          } else {
            this.$router.replace({
              path: `/analytics-configs/${created.id}`,
              query: { tab: 'config' },
            });
          }
        } else {
          await this.$gameApi.patch(`/analytics-configs/${this.$route.params.id}`, payload);
          this.$swal.fire({
            icon: 'success',
            title: 'Saved',
            timer: 1200,
            showConfirmButton: false,
          });
          if (this.safeReturnTo) {
            this.$router.replace(this.safeReturnTo);
          } else {
            await this.load();
          }
        }
      } catch (e) {
        this.$swal.fire({
          icon: 'error',
          title: apiErrorMessage(e, 'Save failed'),
          timer: 3500,
          showConfirmButton: false,
        });
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped>
.analytics-config-form__tabs-shell {
  border: 1px solid rgba(0, 0, 0, 0.22);
  border-radius: 8px;
  overflow: hidden;
  background: #fff;
  margin-bottom: 16px;
}

.analytics-config-form__tabs {
  border: none !important;
  border-bottom: 1px solid rgba(0, 0, 0, 0.12) !important;
  border-radius: 0 !important;
  background: #f5f5f5 !important;
  margin: 0 !important;
}

.analytics-config-form__tab-panel {
  margin: 0;
  border: none;
  border-radius: 0;
  background: #fff;
  min-height: 200px;
  box-shadow: none;
}

.analytics-config-form__tab-panel-body {
  padding: 0;
  overflow-x: auto;
}

.analytics-config-form__tab-panel-body--padded {
  padding: 16px;
}

.analytics-config-form__tab-panel-body ::v-deep > .v-card {
  margin: 0 !important;
  border-radius: 0 !important;
  box-shadow: none !important;
  border: none !important;
}

.analytics-config-form__games {
  overflow: hidden;
}

.analytics-config-form__game-actions {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  white-space: nowrap;
}

.analytics-config-form__games-table ::v-deep .v-data-table__wrapper {
  overflow-x: auto;
}

.analytics-config-form__games-table ::v-deep .analytics-config-form__games-col--compact {
  white-space: nowrap;
  width: 1%;
  padding-left: 8px;
  padding-right: 8px;
}

.analytics-config-form__section {
  border-left: 4px solid #1976d2;
  overflow: hidden;
}

.analytics-config-form__section-title {
  display: flex;
  align-items: center;
  padding-top: 16px !important;
  padding-bottom: 12px !important;
  font-size: 1.15rem !important;
  font-weight: 600 !important;
  letter-spacing: 0.01em;
}

.analytics-config-form__section ::v-deep > .v-card-text {
  padding-top: 4px !important;
  padding-bottom: 12px !important;
}

.analytics-config-form__section ::v-deep .row--dense > .col,
.analytics-config-form__section ::v-deep .row--dense > [class*="col-"] {
  padding-top: 12px !important;
  padding-bottom: 12px !important;
}

.analytics-config-form__save {
  display: flex;
  justify-content: flex-start;
  align-items: center;
  position: sticky;
  bottom: 12px;
  z-index: 2;
  padding: 12px 16px;
  background: rgba(255, 255, 255, 0.92);
  border: 1px solid rgba(25, 118, 210, 0.2);
  border-radius: 8px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
  backdrop-filter: blur(6px);
}

.analytics-config-tab-slide-enter-active,
.analytics-config-tab-slide-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.analytics-config-tab-slide-enter {
  opacity: 0;
  transform: translateX(24px);
}

.analytics-config-tab-slide-leave-to {
  opacity: 0;
  transform: translateX(-24px);
}
</style>
